import { Button } from '@mm/components';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { IS_PROD_URL } from '@mm/utils';
import React, { useEffect, useState } from 'react';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import { useDispatch, useSelector } from 'react-redux';
import { useBookDemo } from '../../../common/buttons/BookDemoButton/use-book-demo';
import { Logo } from '../../../layout/BotBuilderSidebar/Logo/Logo';
import { useRegisterSignupEvent } from '../../hooks/use-register-signup-event';
import { useGetWebsiteTraffic } from '../XRayOnboarding/hooks/use-get-website-traffic';
import { trafficTotalSelector } from '@mm/state/selectors/websiteReportSummarySelector';
import {
  extractDomainName,
  LLR_DEMO_UTM,
  usCountryCode,
} from '../XRayOnboarding/WebsiteReportSummary/constants';
import { ReportHeader } from '../XRayOnboarding/WebsiteReportSummary/ReportHeader/ReportHeader';
import styles from './BookCalendlyMeeting.module.css';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import userGeodataCRUDActions from '@mm/state/actions/UserGeodataCRUDActions';

export const BookCalendlyMeeting = () => {
  const activeCompany = useSelector(activeCompanySelector);
  const session = useSelector(selectSessionSelector);
  const signupFromReportPage =
    activeCompany?.additionalFeatures?.includes('xray_onboarding');

  const { demoUrl, discoUrl, isEligibleForDemo } = useBookDemo();

  const devDemoUrl = `${'https://calendly.com/isupranovich-efe/30min'}?${LLR_DEMO_UTM(
    activeCompany?.website || '',
  )}`;
  const devDiscoUrl = `${'https://calendly.com/osviridenko/15-mins-session'}?${LLR_DEMO_UTM(
    activeCompany?.website || '',
  )}`;

  const { registerSignUpEventWithTracking } = useRegisterSignupEvent();
  const iframeTitle = 'calendly-onboarding';
  const { retrieveTotals } = useGetWebsiteTraffic({});
  const domainName = extractDomainName(activeCompany?.website || '');
  const dispatch: Dispatch<StoreState> = useDispatch();
  const userGeodata = useSelector((state: StoreState) => state.userGeodata);
  const totalsRequestTraffic = useSelector(trafficTotalSelector);
  const totalTraffic = activeCompany?.trafficData || totalsRequestTraffic;

  const [currentType, setCurrentType] = useState<
    'demo' | 'disco' | undefined
  >();

  useEffect(() => {
    const fetchTotals = async () => {
      if (domainName) {
        const { totals, data } = await retrieveTotals(domainName);
        if (totals?.trafficType?.sql) {
          setCurrentType('demo');
        } else {
          data?.country_code === usCountryCode && setCurrentType('disco');
        }
      }
    };

    const fetchUserData = async () => {
      const data = Object.keys(userGeodata).length
        ? userGeodata
        : await dispatch(userGeodataCRUDActions.getData());

      if (data?.country_code === usCountryCode) {
        setCurrentType('disco');
      }
    };

    if (!Object.keys(totalTraffic).length) {
      fetchTotals();
    } else {
      if (totalTraffic.trafficType?.sql) {
        setCurrentType('demo');
      } else {
        fetchUserData();
      }
    }
  }, []);

  useCalendlyEventListener({
    onEventScheduled: () =>
      setTimeout(() => registerSignUpEventWithTracking(), 2000),
  });

  const linkSettings = [
    {
      key: 'disco',
      url: IS_PROD_URL ? demoUrl : devDiscoUrl,
      customAnswers: {},
    },
    {
      key: 'demo',
      url: IS_PROD_URL ? discoUrl : devDemoUrl,
      customAnswers: {
        a1: activeCompany?.name,
        a2: activeCompany?.phoneNumber || '',
        a3: activeCompany?.website,
      },
    },
  ];

  const currentSettings = linkSettings.find(
    setting => setting.key === currentType,
  );
  return (
    <div className={styles.wrapper}>
      <div className={styles.navbar}>
        {' '}
        {signupFromReportPage ? (
          <ReportHeader activeStep={3} isElegibleForDemo={isEligibleForDemo} />
        ) : (
          <Logo className={styles.desktopLogo} />
        )}
      </div>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            {domainName && <div className={styles.website}>{domainName}</div>}
            <div className={styles.heading}>
              Schedule a demo for setting up your account{' '}
              <Button
                color="secondary-blue"
                css={{ container: styles.secondaryButton }}
                onClick={() => registerSignUpEventWithTracking()}
              >
                Skip This Step
              </Button>
            </div>
          </div>
        </div>
        {currentSettings && (
          <InlineWidget
            url={currentSettings.url}
            styles={{ height: '100%' }}
            iframeTitle={iframeTitle}
            prefill={{
              name: `${session.firstName} ${session.lastName}`,
              firstName: session.firstName,
              lastName: session.lastName,
              email: session.email,
              customAnswers: currentSettings.customAnswers,
            }}
          />
        )}
      </div>
    </div>
  );
};
